import { jsx as _jsx } from "react/jsx-runtime";
import { SortableTableHeader } from "@common/components/SortableTableHeader";
import { AbnCell, AccountCell, BsbCell, DetailsToggleCell, GstCell } from "./BankDetailsCell";
import { DocIdCell } from "./DocIdCell";
import { DoctorAvatar } from "./DoctorAvatar";
import { SyncStateCell } from "./SyncStateCell";
// TODO(sap-sync-flag): remove these checks when the SAP sync feature flag is removed
import { isSapProfileSyncEnabledForAnyDoc, isSapProfileSyncEnabledForDoc, } from "../feature_flag.generated";
import { SapBpIdCell } from "./SapBpIdCell";
import { getSapSyncLevel } from "./sapSync/model";
import { SapSyncLevelBadge } from "./sapSync/SapSyncLevelBadge";
export const STATUS_FILTER_OPTIONS = [
    { key: "all", text: "All", value: "all" },
    { key: "active", text: "Active", value: "active" },
    { key: "inactive", text: "Inactive", value: "inactive" },
];
export const HAS_PROFILE_FILTER_OPTIONS = [
    { key: "all", text: "All", value: "all" },
    { key: "has_profile", text: "Has Profile", value: "has_profile" },
    { key: "no_profile", text: "No Profile", value: "no_profile" },
];
export const DOCTOR_TYPE_FILTER_OPTIONS = [
    { key: "all", text: "All", value: "all" },
    { key: "GP", text: "GP", value: "GP" },
    { key: "Cosmetic", text: "Cosmetic", value: "Cosmetic" },
];
// Whether the doctor is inside the SAP sync feature flag at all - a separate question from the
// SAP Status one below, which is about how far a doctor already inside it has got.
export const SAP_SYNC_FILTER_OPTIONS = [
    { key: "all", text: "All", value: "all" },
    { key: "enabled", text: "Sync enabled", value: "enabled" },
    { key: "disabled", text: "Sync disabled", value: "disabled" },
];
// The options are the badges the SAP Status column can show, so the two can't drift apart -
// renaming a state backend-side fails the type check here. "syncing" is listed where a hand-off
// falls in the workflow: after approval, on its way to synced.
export const SAP_STATUS_FILTER_OPTIONS = [
    { key: "all", text: "All", value: "all" },
    { key: "pending", text: "Pending", value: "pending" },
    { key: "approved", text: "Approved", value: "approved" },
    { key: "syncing", text: "Syncing", value: "syncing" },
    { key: "synced", text: "Synced", value: "synced" },
    { key: "quarantined", text: "Quarantined", value: "quarantined" },
];
export const formatBsb = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    return digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
};
// Bulk profile data (docpay/profiles/list) only carries doc_id + last_update_date today -
// bank-detail columns (account/BSB/ABN/GST) live behind the per-doctor endpoint, so they're
// fetched and merged into each row's `bank` field only once "Show details" is clicked.
export const getProfilesListColumns = (sort, sap_sync_enabled) => [
    {
        name: _jsx(SortableTableHeader, { label: "Doctor", field: "name", sort: sort }),
        // Carries what the dropped Active column used to say: a greyed avatar and an "Inactive" note.
        template: ({ row }) => (_jsx(DoctorAvatar, { name: row.name, type: row.type, active: row.active, size: "sm" })),
    },
    {
        name: "Doctor ID",
        jpath: "doc_id",
        className: "w-44",
        template: ({ row }) => _jsx(DocIdCell, { row: row }),
    },
    {
        name: "Account",
        template: ({ row }) => _jsx(AccountCell, { row: row }),
    },
    {
        name: "BSB",
        template: ({ row }) => _jsx(BsbCell, { row: row }),
    },
    {
        name: "ABN",
        template: ({ row }) => _jsx(AbnCell, { row: row }),
    },
    {
        name: "GST",
        template: ({ row }) => _jsx(GstCell, { row: row }),
    },
    {
        name: _jsx(SortableTableHeader, { label: "Last Updated", field: "last_update_date", sort: sort }),
        jpath: "last_update_date",
        type: "date",
    },
    ...(isSapProfileSyncEnabledForAnyDoc(sap_sync_enabled)
        ? [
            {
                name: "SAP Enabled",
                // Wide enough for the longest badge ("Profile & invoices")
                className: "w-44",
                // Nothing to sync without a profile, so there's no level worth showing
                template: ({ row }) => row.has_profile ? (_jsx(SapSyncLevelBadge, { level: getSapSyncLevel(sap_sync_enabled, row.doc_id) })) : null,
            },
            {
                name: "SAP BP ID",
                // Wide enough for a BP ID plus its copy affordance.
                className: "w-32",
                template: ({ row }) => _jsx(SapBpIdCell, { row: row }),
            },
            {
                name: "SAP Status",
                // Wide enough for the longest badge ("Quarantined") plus the failed-sync warning icon.
                className: "w-40",
                template: ({ row }) => isSapProfileSyncEnabledForDoc(sap_sync_enabled, row.doc_id) ? (_jsx(SyncStateCell, { row: row })) : null,
            },
        ]
        : []),
    {
        name: "Profile details",
        // Wide enough for the header, and for either of the buttons underneath it.
        className: "w-40",
        template: ({ row, onToggleDetails, onAddProfile, }) => _jsx(DetailsToggleCell, { row: row, onToggleDetails: onToggleDetails, onAddProfile: onAddProfile }),
    },
];
